import React, { useState } from 'react';
import { BannerAlert, Text } from '@amzn/storm-ui';
import { bannerMetricsPublisher, PartitionedBanners } from '../types';
import { BANNER_LEVEL, BANNER_TYPE } from 'src/app/slice';
import { BannersModal } from '../Common/BannersModal';
import { getTaktAttributes, logWithTakt } from 'src/commons/utils/logWithTakt';
import { BannerText, StyledAlert } from '../styles';
import {
  useHeaderBundle,
  usePageHeaderBundle,
} from 'src/providers/PantherLocalizationProvider';
import { BannerLink } from 'src/commons/components/ConsolidatedBanner/styles';
import { chromeComponents } from 'src/constants';
import { StyledLink } from 'src/commons/components/StyledLink';

interface ConsolidatedBannerProps {
  partitionedBanners: PartitionedBanners;
  onBannerDismiss: () => void;
  bannerLevel: BANNER_LEVEL;
}

export const ConsolidatedBanner: React.VFC<ConsolidatedBannerProps> = ({
  partitionedBanners,
  onBannerDismiss,
  bannerLevel,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [canDismissBanner, setCanDismissBanner] = useState(false);
  const pageHeaderTranslation = usePageHeaderBundle();
  const headerTranslation = useHeaderBundle();
  const bannerType =
    partitionedBanners.error.length > 0
      ? BANNER_TYPE.ERROR
      : partitionedBanners.warning.length > 0
        ? BANNER_TYPE.WARNING
        : BANNER_TYPE.INFO;
  const bannerText = pageHeaderTranslation
    .getMessage('page-header-banner-experiencing-issues-text')
    ?.replace('${x}', partitionedBanners.total.toString());
  const isConsoleLevel = bannerLevel === BANNER_LEVEL.CONSOLE;

  const learnMoreAriaLabel = headerTranslation
    .getMessage('console-banner-learn-more-multiple-messages-aria-label')
    ?.replace('${x}', partitionedBanners.total.toString());

  const toggleModal = (event) => {
    event?.preventDefault();
    const nextIsModalOpen = !isModalOpen;
    if (nextIsModalOpen) {
      const bannerLevelWithFirstLetterCapitalized =
        bannerLevel.charAt(0).toUpperCase() + bannerLevel.slice(1);
      bannerMetricsPublisher[bannerLevel].publishCounterMonitor(
        `bannerLinkClicked:Consolidated${bannerLevelWithFirstLetterCapitalized}LevelBanner`,
        1,
      );
      logWithTakt(`${bannerLevel}BannerClicked`, {
        bannerId: `Consolidated${bannerLevelWithFirstLetterCapitalized}Banner`,
      });
    }

    if (
      !canDismissBanner &&
      nextIsModalOpen &&
      bannerType !== BANNER_TYPE.ERROR
    ) {
      setCanDismissBanner(true);
    }
    setIsModalOpen(nextIsModalOpen);
  };

  return (
    <>
      {isConsoleLevel ? (
        <BannerAlert
          data-test-id="consolidated-banners"
          variant={bannerType}
          withCloseButton={canDismissBanner}
          onClose={onBannerDismiss}
        >
          {isConsoleLevel && <BannerText>{bannerText} </BannerText>}
          <BannerLink
            href="#"
            onClick={toggleModal}
            aria-label={learnMoreAriaLabel}
            isRenderedInModal={false}
            {...getTaktAttributes(
              'console-banner-learn-more-btn',
              chromeComponents.HEADER,
            )}
          >
            {pageHeaderTranslation.getMessage(
              'page-header-banner-learn-more-text',
            )}
          </BannerLink>
        </BannerAlert>
      ) : (
        <StyledAlert
          data-test-id="consolidated-banners"
          variant={bannerType}
          withCloseButton={canDismissBanner}
          onClose={onBannerDismiss}
          header={bannerText}
        >
          {isConsoleLevel && <Text inline>{bannerText} </Text>}
          <StyledLink
            href="#"
            onClick={toggleModal}
            aria-label={learnMoreAriaLabel}
            {...getTaktAttributes(
              'page-banner-learn-more-btn',
              chromeComponents.HEADER,
            )}
          >
            {pageHeaderTranslation.getMessage(
              'page-header-banner-learn-more-text',
            )}
          </StyledLink>
        </StyledAlert>
      )}
      <BannersModal
        partitionedBanners={partitionedBanners}
        header={bannerText}
        isModalOpen={isModalOpen}
        onClose={toggleModal}
        bannerLevel={bannerLevel}
      />
    </>
  );
};
